import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
  Typography,
  Button,
  InputAdornment,
  TextField,
  Tooltip,
  Autocomplete,
  Divider,
  Drawer,
} from "@mui/material";
import "../../App.css";
import dayjs from "dayjs";
import { TableNodata } from "../../components/TableNodata";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api/client";
import { TableLoader } from "../../components/TableLoader";
import { OverAllLoader } from "../../components/OverAllLoader";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useSnackbar } from "../../providers/SnackbarProvider";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CustomPagination from "../../components/CustomPagination";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import useDebounce from "../../hooks/useDebounce";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";

const InstituteAdminList = () => {

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  // ===== states ======
  const tableref = useRef({});
  const navigate = useNavigate();
  const [showTable, setShowTable] = useState(false);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(+localStorage.getItem("currentPage") || 0);
  const [rowsPerPage, setRowsPerPage] = useState(
    +localStorage.getItem("rowsPerPage") || 10
  );
  const [listData, setListData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [overAllLoading, setOverAllLoading] = useState(false);
  const [count, setCount] = useState(0);
  const location = useLocation();
  const { instituteId, dashboard, institute_Name } = location?.state || "";
  const [searchkey, setSearchkey] = useState(
    localStorage.getItem("searchvalue") || ""
  );
  const searchDebounce = useDebounce(searchkey);
  const [selectedCollege, setSelectedCollege] = useState(
    instituteId
      ? instituteId
      : +localStorage.getItem("Institute_Id_Filter") || ""
  );
  const [selectedCollegeName, setSelectedCollegeName] = useState(null);
  const [appliedInstitute, setAppliedInstitute] = useState(
    institute_Name
      ? institute_Name
      : localStorage.getItem("applyInstitute")
        ? localStorage.getItem("applyInstitute")
        : null
  );
  const [colleges, setColleges] = useState([]);
  const { showSnackbar } = useSnackbar();
  // ===== Functions =======
  const handleAddInstituteAdmin = (boolean, id) => {
    navigate("/addInstituteAdmin", {
      state: {
        addAdmin: boolean,
        adminID: id,
      },
    });
  };

  const handleSearchChange = (e) => {
    localStorage.setItem("currentPage", 0);
    setPage(0);
    setSearchkey(e.target.value);
    localStorage.setItem("searchvalue", e.target.value);
  };

  const handleClickClear = () => {
    tableref.current = {};
    setSelectedCollege(null);
    setSelectedCollegeName(null);
    setAppliedInstitute(null);
    localStorage?.removeItem("applyInstitute");
    localStorage?.removeItem("Institute_Id_Filter");

    navigate(location.pathname, {
      replace: true,
      state: {
        ...location.state,
        testType: null,
        institute_Name: null,
        instituteId: null,
      },
    });
  };

  const handleApplyInstitute = () => {
    tableref.current = {};
    setPage(0);

    setAppliedInstitute(selectedCollegeName);
    localStorage.setItem("applyInstitute", selectedCollegeName);

    setOpen(false);
  };

  //======= API =======

  const fetchInstistuteAdminList = async () => {
    setLoading(true);

    const cachekey = `page_${page}_size_${rowsPerPage}_searchkey_${searchDebounce}_selectedCollege_${selectedCollege}`;

    if (tableref.current[cachekey]) {
      const cached = tableref.current[cachekey];
      setListData(cached?.data);
      setCount(cached?.count);
      setLoading(false);
      setOverAllLoading(false);
      return;
    }

    try {
      const payload = {
        page: page,
        size: rowsPerPage,
        search_key: searchDebounce,
        institute_id: selectedCollege || null,
      };
      const res = await api.post("ListInstituteAdmins", payload);

      const data = res?.data?.data;
      const count = res?.data?.count;

      tableref.current[cachekey] = {
        data,
        count,
      };
      setListData(res?.data?.data);
      setCount(res?.data?.count);
      setLoading(false);
      setOverAllLoading(false);
    } catch (error) {
      console.log("Error :", error);
      setLoading(false);
      setOverAllLoading(false);
      showSnackbar("error", "Failed to fetch data. Please try again.");
    }
  };

  const instituteList = async () => {
    try {
      const res = await api.post("/ListInstituteNames");
      if (res?.data?.status === "success") {
        setColleges(res?.data?.data);
      } else {
        showSnackbar("error", res?.data?.message);
      }
    } catch (error) {
      console.log("Error :", error);
      showSnackbar("error", "Failed to fetch institutes.");
    }
  };

  //====== UseEffect ======

  useEffect(() => {
    if (!overAllLoading) {
      fetchInstistuteAdminList();
    }
  }, [page, rowsPerPage, searchDebounce, appliedInstitute]);

  useEffect(() => {
    setOverAllLoading(true);
    fetchInstistuteAdminList();
    instituteList();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowTable(true);
    }, 200); // 0.2 sec delay

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    localStorage.setItem("currentPage", page);
    localStorage.setItem("rowsPerPage", rowsPerPage);
  }, [page, rowsPerPage]);

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: 320,
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            height: "100vh",
            borderRadius: "12px 0 0 12px",
          },
        }}
      >
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <FilterListIcon color="primary" />
            <Typography variant="h6" fontWeight={600} color="primary">
              Filters
            </Typography>
          </Box>

          <IconButton aria-label="Close" size="small" onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        {/* ONE DROPDOWN */}
        <Box display="flex" flexDirection="column" gap={0.8}>
          <Typography variant="subtitle2" fontWeight={600}>
            {`${getLabel('INSTITUTE')}`}
          </Typography>
          {localStorage.getItem("role") === "SUPER_ADMIN" && (
            <Autocomplete
              size="small"
              sx={{ minWidth: 250 }}
              options={colleges} // full array of objects
              getOptionLabel={(option) => option?.name || ""} // display the name
              value={
                colleges.find(
                  (college) => college.institute_id === selectedCollege
                ) || null
              } // show selected one
              onChange={(event, newValue) => {
                localStorage.setItem(
                  "Institute_Id_Filter",
                  newValue ? newValue.institute_id : ""
                );
                setPage(0);
                setSelectedCollege(newValue ? newValue.institute_id : ""); // store ID
                setSelectedCollegeName(newValue ? newValue.name : ""); // store ID
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={`Select ${getLabel('INSTITUTE')}`}
                  size="small"
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          )}
        </Box>
        {/* Apply Button */}
        <Button
          variant="contained"
          color="primary"
          fullWidth
          className="customButton"
          onClick={handleApplyInstitute}
        >
          Apply Filters
        </Button>
      </Drawer>
      {dashboard === true && (
        <Grid
          item
          sx={{
            display: "flex",
            alignItems: "center",
            color: "#1c97e3",
            gap: "4px",
            cursor: "pointer",
            width: "fit-content",
            mb: 2,
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              window.history.back();
            }
          }}
          onClick={() => window.history.back()}
        >
          <ArrowBackIcon sx={{ fontSize: "17px" }} />
          <Typography
            sx={{ fontWeight: 600, fontSize: "14px", color: "#1c97e3" }}
          >
            Back
          </Typography>
        </Grid>
      )}
      {overAllLoading ? (
        <OverAllLoader message="Please wait..." />
      ) : (
        <Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
            sx={{
              // borderBottom: "1px solid #828487",
              pb: 2,
            }}
          >
            <Grid
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 0.5,
                alignItems: "center",
              }}
            >
              <Box className="icon-box">
                <AdminPanelSettingsIcon className="iconHeading" />
              </Box>
              {/* Title */}
              <Typography
                sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: "#0a0a0a" }}
              >
                {getLabel("INSTITUTE_ADMIN")}
              </Typography>
            </Grid>
            {/* Search + Button */}
            <Box display="flex" gap={4} alignItems="center">
              {/* Search Field */}
              <TextField
                value={searchkey}
                size="small"
                variant="outlined"
                placeholder={`Search ${getLabel("INSTITUTE_ADMIN")}`}
                onChange={handleSearchChange}
                className="custom-search-field"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                className="gradient-button"
                onClick={() => handleAddInstituteAdmin("true", null)}
              >
                Add Admin
              </Button>
            </Box>
          </Box>
          {/* {dashboard !== true && (
            <Box display="flex" gap={1} alignItems="center">
              <Box display="flex" alignItems="center" gap={0.5}>
                <FilterAltIcon color="primary" />
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: "primary.main",
                    whiteSpace: "nowrap",
                  }}
                >
                  Filters
                </Typography>
              </Box>
              {localStorage.getItem("role") === "SUPER_ADMIN" && (
                <Autocomplete
                  size="small"
                  sx={{ minWidth: 250 }}
                  options={colleges} // full array of objects
                  getOptionLabel={(option) => option?.name || ""} // display the name
                  value={
                    colleges.find(
                      (college) => college.institute_id === selectedCollege
                    ) || null
                  } // show selected one
                  onChange={(event, newValue) => {
                    localStorage.setItem(
                      "Institute_Id_Filter",
                      newValue ? newValue.institute_id : ""
                    );
                    setPage(0);
                    setSelectedCollege(newValue ? newValue.institute_id : ""); // store ID
                  }}
                  renderInput={(params) => (
                    <TextField
                      size="small"
                      className="custom-search-field"
                      {...params}
                      placeholder="All Institutes"
                      variant="outlined"
                    />
                  )}
                />
              )}
            </Box>
          )} */}

          <TableContainer
            component={Paper}
            sx={{
              mt: 3,
              opacity: showTable ? 1 : 0,
              transform: showTable ? "translateY(0)" : "translateY(40px)",
              transition: "opacity 0.5s ease, transform 0.5s ease",
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {appliedInstitute && appliedInstitute !== "null" && (
                          <Box className="filter-chip">
                            <Typography
                              sx={{ fontSize: "13px", fontWeight: 500 }}
                            >
                              {localStorage.getItem("applyInstitute") ||
                                appliedInstitute}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setAppliedInstitute(null);
                                setSelectedCollegeName(null);
                                setSelectedCollege(null);
                                localStorage.removeItem("applyInstitute");
                                localStorage?.removeItem("Institute_Id_Filter");
                                navigate(location.pathname, {
                                  replace: true,
                                  state: {
                                    ...location.state,
                                    testType: null,
                                    institute_Name: null,
                                    instituteId: null,
                                  },
                                });
                              }}
                              sx={{ ml: 0.5 }}
                            >
                              <CloseIcon sx={{ fontSize: "14px" }} />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          onClick={() => setOpen(true)}
                          color="primary"
                          className="more-filter-btn"
                        >
                          <FilterListIcon />
                          <Typography className="more-filter-text">
                            More filter
                          </Typography>
                        </IconButton>

                        {appliedInstitute !== null && (
                          <Button
                            variant="outlined"
                            className="clear-filter-btn"
                            onClick={handleClickClear}
                          >
                            Clear Filter
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow className="tableHeader">
                  <TableCell className="tableHeader">
                    <b>S.No</b>
                  </TableCell>
                  <TableCell className="tableHeader">
                    <b>Name</b>
                  </TableCell>
                  <TableCell className="tableHeader">
                    <b>Contact</b>
                  </TableCell>
                  <TableCell className="tableHeader">
                    <b>{`${getLabel('INSTITUTE')}`}</b>
                  </TableCell>
                  <TableCell className="tableHeader">
                    <b>Address</b>
                  </TableCell>
                  {/* <TableCell>
                    <b>Created By</b>
                  </TableCell> */}
                  <TableCell className="tableHeader">
                    <b>Created Date</b>
                  </TableCell>
                  <TableCell className="tableHeader">
                    <b>Status</b>
                  </TableCell>
                  <TableCell className="tableHeader">
                    <b>Actions</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              {loading ? (
                <TableCell colSpan={8} align="center">
                  <TableLoader message="Fetching institute admins..." />
                </TableCell>
              ) : (
                <TableBody className="tablebody">
                  {listData.map((item, index) => (
                    <TableRow
                      key={item.assessment_id}
                      hover
                      sx={{
                        backgroundColor:
                          index % 2 === 1 ? "#FAFAFA" : "inherit",
                        "&:hover": { backgroundColor: "#f1f1f1" },
                      }}
                    >
                      <TableCell align="center">
                        {page * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell>
                        <span style={{ fontWeight: "bold" }}>
                          {item.first_name + " " + item.last_name}
                        </span>
                        <br />
                        {item?.gender}
                      </TableCell>
                      <TableCell>
                        {item?.phone}
                        <br />
                        {item?.email}
                      </TableCell>
                      <TableCell>{item.institute_name}</TableCell>
                      <TableCell>
                        {item.city}
                        <br />
                        {item?.state}
                        <br />
                        {item?.pincode}
                      </TableCell>
                      {/* <TableCell>{item.created_by}</TableCell> */}
                      <TableCell sx={{ textWrap: "nowrap" }}>
                        {item?.created_at
                          ? dayjs(item.created_at).format("DD-MM-YYYY")
                          : "-"}
                      </TableCell>

                      <TableCell>
                        <Box
                          sx={{
                            display: "inline-block",
                            px: 2,
                            py: 0.5,
                            borderRadius: "12px",
                            fontWeight: "bold",
                            fontSize: "12px",
                            backgroundColor:
                              item.status === "ACTIVE"
                                ? "#d4edda"
                                : item.status === "INACTIVE"
                                  ? "#f8d7da"
                                  : "#f1f1f1",
                            color:
                              item.status === "ACTIVE"
                                ? "#155724"
                                : item.status === "INACTIVE"
                                  ? "#721c24"
                                  : "#333",
                            textAlign: "center",
                          }}
                        >
                          {item.status}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title="View"
                          arrow
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: "primary.main",
                                color: "white",
                                fontSize: 13,
                                fontWeight: 500,
                              },
                            },
                            arrow: {
                              sx: {
                                color: "primary.main",
                              },
                            },
                          }}
                        >
                          <IconButton
                            color="primary"
                            // onClick={() =>
                            //   handleAddInstituteAdmin("false", item?.user_id)
                            // }
                            onClick={() =>
                              navigate("/viewInstituteAdmin", {
                                state: {
                                  adminID: item?.user_id,
                                },
                              })
                            }
                            sx={{
                              display: "flex",
                              // alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <VisibilityIcon />
                            <Typography
                              color="primary"
                              sx={{ fontSize: "12px" }}
                            >
                              View
                            </Typography>
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}

                  {((page === 0 && listData?.length > rowsPerPage - 1) ||
                    page !== 0) && (
                      <CustomPagination
                        page={page}
                        rowsPerPage={rowsPerPage}
                        count={count}
                        setPage={setPage}
                        colSpan={8}
                      />
                    )}

                  {listData?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <TableNodata />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default InstituteAdminList;
